import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopCustomerLinks = () => {
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState({});
  const [results, setResults] = useState({});
  const load = () => axios.get(`${API}/shop/admin/customers`).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const search = async (cid, q) => {
    setPick({ ...pick, [cid]: q });
    if (q.length < 2) return setResults({ ...results, [cid]: [] });
    const { data } = await axios.get(`${API}/customers/search`, { params: { q } });
    setResults({ ...results, [cid]: (Array.isArray(data) ? data : data.customers || []).slice(0, 6) });
  };
  const link = async (cid, posId) => {
    try { await axios.put(`${API}/shop/admin/customers/${cid}/link`, { pos_customer_id: posId }); toast.success(posId ? "Linked — they now see their discount" : "Unlinked"); setResults({}); setPick({}); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not link"); }
  };
  return (
    <div data-testid="shop-customer-links">
      <p className="mb-2 font-semibold">Shop customers ({rows.length}) — link to a store account to give them their discount online</p>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.customer_id} className="rounded-lg border p-2 text-sm" data-testid={`shop-customer-${r.email}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span><b>{r.name}</b> · {r.email} {r.phone ? `· ${r.phone}` : ""}</span>
              {r.pos_customer_id ? (
                <span className="flex items-center gap-2 text-emerald-700"><Link2 className="h-4 w-4" /> {r.pos_customer_name || r.pos_customer_id}
                  <button type="button" className="text-xs text-rose-600" onClick={() => link(r.customer_id, null)} data-testid={`shop-unlink-${r.email}`}>unlink</button></span>
              ) : (
                <Input className="h-8 w-56" placeholder="Search store customer" value={pick[r.customer_id] || ""} onChange={(e) => search(r.customer_id, e.target.value)} data-testid={`shop-link-search-${r.email}`} />
              )}
            </div>
            {(results[r.customer_id] || []).map((c) => (
              <button key={c.id} type="button" className="mt-1 block w-full rounded px-2 py-1 text-left hover:bg-slate-100" onClick={() => link(r.customer_id, c.id)} data-testid={`shop-link-pick-${c.id}`}>
                {c.name} {c.account_number ? `(${c.account_number})` : ""} {c.email ? `· ${c.email}` : ""}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const OnlineShopSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/shop/admin/settings`).then(({ data }) => setS(data)).catch(() => {}); }, []);
  const save = async (patch) => {
    try { const { data } = await axios.put(`${API}/shop/admin/settings`, patch); setS(data); toast.success("Shop settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Only an admin can change shop settings"); }
  };
  if (!s) return null;
  return (
    <Card data-testid="online-shop-settings"><CardContent className="space-y-4 p-4">
      <p className="flex items-center gap-2 text-lg font-bold"><Settings className="h-5 w-5" /> Shop settings</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant={s.enabled ? "default" : "outline"} onClick={() => save({ enabled: !s.enabled })} data-testid="shop-toggle-enabled">{s.enabled ? "Shop is OPEN" : "Shop is CLOSED"}</Button>
        <Button variant={s.mode === "in_stock" ? "default" : "outline"} onClick={() => save({ mode: "in_stock" })} data-testid="shop-mode-in-stock">All in-stock products</Button>
        <Button variant={s.mode === "selected" ? "default" : "outline"} onClick={() => save({ mode: "selected" })} data-testid="shop-mode-selected">Only products I choose</Button>
        <span className="text-sm text-slate-600" data-testid="shop-visible-count">{s.visible_count} products online</span>
      </div>
      <p className="text-xs text-slate-500">Hide or choose products in Product Master → Other tab → Online shop.</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">Collection hours<Input className="mt-1 w-80" defaultValue={s.collection_hours} onBlur={(e) => e.target.value !== s.collection_hours && save({ collection_hours: e.target.value })} data-testid="shop-collection-hours" /></label>
      </div>
      <ShopCustomerLinks />
    </CardContent></Card>
  );
};
